import { ChangeEvent, useEffect, useState } from 'react'
import { Box, InputGroup, InputRightElement, Spinner, Stack } from '@chakra-ui/react'

import { cepMask } from 'utils/masks'
import { MaskedInput } from 'components/dataEntry'
import { removeEverythingThatIsNotNumber } from 'utils/transforms/string'
import CustomFormFieldErrorMessage from 'components/tools/CustomForm/commonComponents/CustomFormFieldErrorMessage'
import { CustomFormViewerQuestionProps } from 'components/tools/CustomForm/types/CustomFormViewerQuestionProps'
import { useCustomFormViewer } from 'components/tools/CustomForm/components/Viewer/contexts/CustomFormViewerManager/Provider'
import CustomFormAddressByCEP from 'components/tools/CustomForm/commonComponents/CustomFormAddressByCEP'
import useGetAddressByCEP from 'components/tools/CustomForm/hooks/useGetAddressByCEP'
import validateCEPOnCustomForm from 'components/tools/CustomForm/components/Viewer/utils/validateCEPOnCustomForm'
import useCustomFormGetDefaultValueAsArray from '../../hooks/useCustomFormGetDefaultValueAsArray'

export default function CustomFormCEPQuestion({
  question,
  questionKey,
  disabled,
  formMethods,
}: CustomFormViewerQuestionProps) {
  const [cepNumber, setCEPNumber] = useState<string>('')

  const initialValues = ['', '', '', '', '', '', '', '']

  const { removeErrorFromQuestion, setErrorOnQuestion, thisQuestionHasError } = useCustomFormViewer()

  const questionWithError = thisQuestionHasError(question.slug)?.question

  const { addressError, handleGetAddressByCEP, loadingAddress, address, setAddress } = useGetAddressByCEP()

  async function handleOnChangeCEP(event: ChangeEvent<HTMLInputElement>) {
    const cep = event.target.value
    setCEPNumber(cep)
  }

  async function setCepValuesByAPI(paramsCepNumber: string) {
    const errorMessage = validateCEPOnCustomForm({ cep: paramsCepNumber, isRequired: question.is_required })

    if (errorMessage === '') {
      const addressObject = await handleGetAddressByCEP(paramsCepNumber)
      if (addressObject.addressError === '') {
        removeErrorFromQuestion(question.slug)
      }
    } else {
      setAddress({})
      setErrorOnQuestion(question, errorMessage)
    }
  }

  async function handleOnBlurCEP() {
    setCepValuesByAPI(cepNumber)
  }

  const { defaultValue } = useCustomFormGetDefaultValueAsArray({ question, questionKey, disabled })

  useEffect(() => {
    if (formMethods?.setValue) {
      if (question?.answer) {
        const [cep, street, neighborhood, city, state, country, latitude, longitude] = question.answer
        formMethods?.setValue(questionKey, question.answer)
        setCEPNumber(cepMask(cep))
        setAddress({ street, neighborhood, city, state, country, latitude, longitude })
      } else {
        formMethods?.setValue(questionKey, initialValues)
      }
    }
  }, [question])

  useEffect(() => {
    if (defaultValue.length > 0) {
      const defaultCepNumber = defaultValue[0]
      setCEPNumber(defaultCepNumber)
      setCepValuesByAPI(defaultCepNumber)
    }
  }, [defaultValue])

  useEffect(() => {
    if (addressError !== '') {
      setErrorOnQuestion(question, addressError)
    }
  }, [addressError])

  useEffect(() => {
    const cepOnlyNumber = removeEverythingThatIsNotNumber(cepNumber)

    if (formMethods?.setValue) {
      if (address.city) {
        formMethods?.setValue(questionKey, [
          cepOnlyNumber,
          address.street,
          address.neighborhood,
          address.city,
          address.state,
          'Brasil',
          0,
          0,
        ])
      } else {
        formMethods?.setValue(questionKey, initialValues)
      }
    }
  }, [address])

  return (
    <Box>
      <Stack>
        <InputGroup>
          <MaskedInput
            id={question.slug}
            value={cepNumber}
            onChange={handleOnChangeCEP}
            onBlur={handleOnBlurCEP}
            mask={cepMask}
            isDisabled={disabled}
            placeholder={question?.placeholder}
          />

          <InputRightElement>{loadingAddress && <Spinner />}</InputRightElement>
        </InputGroup>

        <CustomFormFieldErrorMessage message={questionWithError?.error_message} />
      </Stack>

      <CustomFormAddressByCEP address={address} />
    </Box>
  )
}
